import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../components/cos-icon.js';
import '../components/cos-skeleton.js';
import type { Crumb } from '../navigation/trail-registry.js';

export type { Crumb };

/**
 * `<cos-trail>` — the ONE breadcrumb trail
 * (docs/design-system/breadcrumb-trail.md). `cos-app-shell` renders it as the
 * first child of its header on every routed page; pages never render
 * breadcrumbs or Back links of their own, and there is no separate Back pill:
 * the first crumb IS the back affordance.
 *
 * Purely presentational. It takes a resolved `crumbs` array (see
 * navigation/trail-registry.ts's `resolveTrail`) and renders it — no routing,
 * no fetching, no knowledge of where a label came from. Links are real
 * `<a href>` so middle-click and copy-link work; the shell's global click
 * interceptor turns a plain click into an SPA navigation.
 *
 * Three things it decides for itself:
 *
 * - **Collapse** (§4.5, §4.6). Five or more crumbs, or a trail that cannot
 *   fit the header, replaces the middle with a `…` button opening a menu of
 *   the hidden crumbs. The section crumb is never collapsed and never
 *   truncated — it is the anchor the whole treatment is named for.
 * - **Compact** (§7). Below the hamburger breakpoint the trail is the
 *   immediate parent alone, with a leading chevron, so it never wraps and
 *   never competes with the hamburger and the avatar for the same row.
 * - **Loading** (§5). A record page's leaf is only known once the record
 *   arrives, so the shell sets `loading` and the leaf renders as a 12ch
 *   skeleton rather than the raw id.
 */
@customElement('cos-trail')
export class CosTrail extends LitElement {
	/** The resolved trail, section first, current page last. */
	@property({ attribute: false }) crumbs: Crumb[] = [];
	/** Phone mode: the immediate parent alone (§7). */
	@property({ type: Boolean, reflect: true }) compact = false;
	/** The leaf has not arrived yet — render a skeleton in its place. */
	@property({ type: Boolean }) loading = false;

	@state() private _menuOpen = false;
	/** Set when the trail measured wider than the header (§4.6). */
	@state() private _overflow = false;
	/** Set when even the COLLAPSED trail does not fit, at which point the
	 *  current crumb starts truncating too. */
	@state() private _tight = false;
	/** The host width at which it last overflowed, so re-expanding is tried
	 *  only when there is genuinely more room than there was. */
	private _overflowAt = 0;
	private _observer: ResizeObserver | null = null;

	static styles = css`
		:host {
			display: flex;
			align-items: center;
			min-width: 0;
			/* The negative margin (§6) pulls the first crumb's padding back so
			   its text aligns with the header's own padding edge. */
			margin-left: calc(var(--cos-space-2) * -1);
			font-size: var(--cos-font-size-sm);
			line-height: 1;
			/* Never a cause of horizontal scroll in the header (§7). */
			overflow: hidden;
		}

		/* The trail keeps its NATURAL width and the host clips it (§4.7:
		   crumbs other than the current one are never truncated, they
		   collapse instead). Letting the flex children shrink instead was a
		   silent bug: every crumb ellipsised to fit and scrollWidth never
		   exceeded clientWidth, so the width-driven collapse never fired and
		   a narrow header showed four unreadable stubs. */
		nav {
			/* Shrinks with the header so the ol below it is the box that
			   actually runs out of room. */
			flex: 0 1 auto;
			min-width: 0;
			display: flex;
			align-items: center;
		}

		ol {
			display: flex;
			align-items: center;
			/* The ol is the one box that shrinks, and it clips rather than
			   squeezing its children: that is what makes "the trail does not
			   fit" measurable (scrollWidth > clientWidth) instead of silently
			   ellipsising every crumb. */
			flex: 0 1 auto;
			min-width: 0;
			overflow: hidden;
			margin: 0;
			padding: 0;
			list-style: none;
			white-space: nowrap;
		}

		li {
			display: flex;
			align-items: center;
			flex: none;
		}

		.sep {
			color: var(--cos-text-tertiary);
			padding: 0 2px;
			user-select: none;
		}

		/* One shared box for a crumb whether it is a link, a button or plain
		   text, so hover and focus land on exactly the same rectangle. */
		.crumb {
			display: inline-flex;
			align-items: center;
			flex: none;
			gap: 6px;
			/* 32px hit target inside the 56px header (§8). */
			min-height: 32px;
			box-sizing: border-box;
			padding: 4px 6px;
			border-radius: var(--cos-radius-sm);
			border: 0;
			background: none;
			font: inherit;
			color: var(--cos-text-secondary);
			text-decoration: none;
			cursor: pointer;
		}

		a.crumb:hover { background: var(--cos-surface-raised); color: var(--cos-text-primary); }
		button.crumb:hover { background: var(--cos-surface-raised); color: var(--cos-text-primary); }

		.crumb:focus-visible {
			outline: 2px solid var(--cos-brand-700);
			outline-offset: 2px;
		}

		/* The section crumb: rail glyph + label, brand ink (§6). The icon
		   inherits currentColor, so it is the rail's own glyph in the trail's
		   colour, never a second icon set. */
		.section {
			color: var(--cos-brand-700);
			font-weight: 500;
		}
		a.section:hover { background: var(--cos-brand-50); color: var(--cos-brand-700); }

		/* The current page — and a single-crumb top-level page, which is the
		   section and the current page at once. */
		.current {
			color: var(--cos-text-primary);
			font-weight: 500;
			cursor: default;
			/* §4.7 — ellipsis at 40 characters, full label in the title. */
			max-width: 40ch;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			display: block;
			line-height: 24px;
		}
		.current.with-icon {
			display: inline-flex;
			align-items: center;
			line-height: 1;
		}

		/* Only the current crumb truncates (the .current rule above); every
		   other label keeps its full width. */
		.label {
			white-space: nowrap;
		}

		/* §4.6's second step: once the middle is already collapsed and the
		   trail STILL does not fit, the current crumb truncates. The section
		   crumb never does. */
		:host([data-tight]) li:last-child { flex: 0 1 auto; min-width: 3ch; }
		:host([data-tight]) .current { max-width: none; }

		/* Collapse control + its menu. */
		.more-wrap { position: relative; display: inline-flex; }
		.more { letter-spacing: 1px; }

		.menu {
			position: absolute;
			top: calc(100% + 4px);
			left: 0;
			z-index: 60;
			min-width: 180px;
			max-width: 320px;
			padding: var(--cos-space-1);
			margin: 0;
			list-style: none;
			background: var(--cos-surface);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-md);
			box-shadow: var(--cos-shadow-md);
		}

		.menu a {
			display: block;
			padding: var(--cos-space-2) var(--cos-space-3);
			border-radius: var(--cos-radius-sm);
			color: var(--cos-text-primary);
			text-decoration: none;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
		.menu a:hover { background: var(--cos-surface-raised); }
		.menu a:focus-visible {
			outline: 2px solid var(--cos-brand-700);
			outline-offset: -2px;
		}

		/* Compact (§7): the immediate parent alone, leading chevron, no
		   section icon, no separator. */
		:host([compact]) .crumb { color: var(--cos-text-primary); }
	`;

	connectedCallback(): void {
		super.connectedCallback();
		document.addEventListener('click', this._onDocumentClick, true);
		document.addEventListener('keydown', this._onKeydown);
		if (typeof ResizeObserver !== 'undefined') {
			this._observer = new ResizeObserver(() => this._measure());
			this._observer.observe(this);
		}
	}

	disconnectedCallback(): void {
		document.removeEventListener('click', this._onDocumentClick, true);
		document.removeEventListener('keydown', this._onKeydown);
		this._observer?.disconnect();
		this._observer = null;
		super.disconnectedCallback();
	}

	protected updated(changed: Map<string, unknown>): void {
		if (changed.has('crumbs')) {
			this._menuOpen = false;
			// A new page is a new measurement; keeping the old verdict would
			// collapse a two-crumb trail because a four-crumb one did not fit.
			this._overflow = false;
			this._tight = false;
			this._overflowAt = 0;
		}
		this._measure();
		this.toggleAttribute('data-tight', this._tight);
	}

	/** Width-driven collapse (§4.6). Compared against the host rather than
	 *  the header because the host is what the header's flex layout actually
	 *  granted the trail once the utilities took their space. */
	private _measure(): void {
		if (this.compact) return;
		const ol = this.shadowRoot?.querySelector('ol');
		if (!ol) return;
		const available = this.clientWidth;
		if (!available) return; // not laid out yet (or a headless test)
		// More room than when we gave up: try the full trail again. If it
		// still does not fit, the next measure re-collapses it at the new
		// width, so this converges rather than oscillating.
		if (this._overflow && available > this._overflowAt) {
			this._overflow = false;
			this._tight = false;
			this._overflowAt = 0;
			return;
		}
		if (ol.scrollWidth <= ol.clientWidth + 1) return;
		if (!this._overflow) {
			this._overflowAt = available;
			this._overflow = true;
			return;
		}
		this._tight = true;
	}

	private _onDocumentClick = (e: Event): void => {
		if (!this._menuOpen) return;
		if (e.composedPath().includes(this)) return;
		this._menuOpen = false;
	};

	private _onKeydown = (e: KeyboardEvent): void => {
		if (e.key === 'Escape' && this._menuOpen) this._menuOpen = false;
	};

	/** Five or more crumbs collapse by rule; fewer collapse only if they do
	 *  not fit (§4.5, §4.6). Either way there must BE a middle to hide. */
	private get _collapsed(): boolean {
		return this.crumbs.length >= 4 && (this.crumbs.length >= 5 || this._overflow);
	}

	private _renderCrumb(crumb: Crumb, opts: { section?: boolean; current?: boolean }): TemplateResult {
		const icon = crumb.icon
			? html`<cos-icon name=${crumb.icon} size="16"></cos-icon>`
			: nothing;
		const classes = ['crumb', opts.section ? 'section' : '', opts.current ? 'current' : ''].filter(Boolean).join(' ');
		if (opts.current) {
			// The leaf is the only crumb that truncates; everything else
			// collapses instead (§4.7).
			const body = this.loading
				? html`<cos-skeleton variant="text" width="12ch" height="14px"></cos-skeleton>`
				: html`<span class="label">${crumb.label}</span>`;
			return html`<span
				class=${classes + (crumb.icon ? ' with-icon' : '')}
				aria-current="page"
				title=${crumb.label}
				>${icon}${body}</span
			>`;
		}
		return html`<a class=${classes} href=${crumb.href ?? ''}>${icon}<span class="label">${crumb.label}</span></a>`;
	}

	/** A crumb with no href and no current flag: an ancestor that is real in
	 *  the hierarchy but has no page of its own ("IMLS PLS data"). */
	private _renderPlain(crumb: Crumb): TemplateResult {
		return html`<span class="crumb">${crumb.label}</span>`;
	}

	private _renderStep(crumb: Crumb, index: number, last: number): TemplateResult {
		if (index === last) return this._renderCrumb(crumb, { current: true });
		if (!crumb.href) return this._renderPlain(crumb);
		return this._renderCrumb(crumb, { section: index === 0 });
	}

	private _renderMore(hidden: Crumb[]): TemplateResult {
		return html`
			<li class="more-wrap">
				<button
					class="crumb more"
					aria-haspopup="menu"
					aria-expanded=${this._menuOpen ? 'true' : 'false'}
					aria-label="Show the rest of the trail"
					@click=${(e: Event) => {
						e.stopPropagation();
						this._menuOpen = !this._menuOpen;
					}}
				>
					…
				</button>
				${this._menuOpen
					? html`<ul class="menu" role="menu">
							${hidden.map(
								(c) => html`<li role="none">
									<a role="menuitem" href=${c.href ?? ''} @click=${() => { this._menuOpen = false; }}
										>${c.label}</a
									>
								</li>`,
							)}
						</ul>`
					: nothing}
			</li>
		`;
	}

	private _renderCompact(): TemplateResult {
		const crumbs = this.crumbs;
		// A top-level page is the section label alone — no chevron, nothing
		// to go back to (§7).
		if (crumbs.length < 2) {
			return html`<nav aria-label="Breadcrumb">
				<ol>
					<li>${this._renderCrumb({ ...crumbs[0], icon: undefined }, { current: true })}</li>
				</ol>
			</nav>`;
		}
		const parent = crumbs[crumbs.length - 2];
		return html`<nav aria-label="Breadcrumb">
			<ol>
				<li>
					<a class="crumb" href=${parent.href ?? ''}>
						<cos-icon name="chevron-left" size="16"></cos-icon>
						<span class="label">${parent.label}</span>
					</a>
				</li>
			</ol>
		</nav>`;
	}

	render() {
		if (!this.crumbs.length) return nothing;
		if (this.compact) return this._renderCompact();

		// Section, …, parent, current (§4.5).
		const visible = this._collapsed
			? [this.crumbs[0], this.crumbs[this.crumbs.length - 2], this.crumbs[this.crumbs.length - 1]]
			: this.crumbs;
		const hidden = this._collapsed ? this.crumbs.slice(1, this.crumbs.length - 2) : [];
		const last = visible.length - 1;

		return html`
			<nav aria-label="Breadcrumb">
				<ol>
					${visible.map((crumb, i) => {
						const sep = i === 0 ? nothing : html`<li class="sep" aria-hidden="true">›</li>`;
						const more = this._collapsed && i === 1 ? html`${this._renderMore(hidden)}<li class="sep" aria-hidden="true">›</li>` : nothing;
						// The original index drives the section styling: after
						// a collapse the first VISIBLE crumb is still crumb 0.
						return html`${sep}${more}<li>${this._renderStep(crumb, i, last)}</li>`;
					})}
				</ol>
			</nav>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-trail': CosTrail;
	}
}
